import { fetchAuthSession } from "aws-amplify/auth";
import { graphqlUserPool } from "./graphqlUserPool";
import { logToConsole } from "./utilities";

export const ACTIVE_LOGIN_SESSION_ID_STORAGE_KEY = "mtam.activeSessionId";

export type LoginEventTypeValue =
  | "LOGIN_SUCCESS"
  | "LOGOUT"
  | "LOGOUT_TIMEOUT";

export type LoginClientTypeValue =
  | "DESKTOP_WEB"
  | "MOBILE_WEB"
  | "TABLET_WEB";

type LoginClientMetadata = {
  clientType: LoginClientTypeValue;
  browser: string;
  os: string;
  userAgent: string;
};

type LogSessionEventArgs = {
  eventType: LoginEventTypeValue;
  userId: string;
  sessionId: string;
  authToken?: string;
};

const createLoginEventMutation = /* GraphQL */ `mutation CreateLoginEvent(
  $input: CreateLoginEventInput!
  $condition: ModelLoginEventConditionInput
) {
  createLoginEvent(input: $input, condition: $condition) {
    id
    __typename
  }
}`;

function getUserAgent() {
  if (typeof navigator === "undefined") {
    return "unknown";
  }

  return navigator.userAgent || "unknown";
}

export function createLoginSessionId() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

export async function getCurrentAuthToken() {
  try {
    return (await fetchAuthSession()).tokens?.idToken?.toString();
  } catch (error) {
    logToConsole("warn", "Failed to capture auth token for login event logging:", error);
    return undefined;
  }
}

export function getStoredActiveSessionId() {
  if (typeof window === "undefined") {
    return null;
  }

  const sessionId = window.localStorage.getItem(ACTIVE_LOGIN_SESSION_ID_STORAGE_KEY);
  return sessionId?.trim() || null;
}

export function setStoredActiveSessionId(sessionId: string) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(ACTIVE_LOGIN_SESSION_ID_STORAGE_KEY, sessionId);
}

export function clearStoredActiveSessionId() {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(ACTIVE_LOGIN_SESSION_ID_STORAGE_KEY);
}

export function getLoginClientMetadata(userAgent = getUserAgent()): LoginClientMetadata {
  const normalizedUserAgent = userAgent.toLowerCase();

  let clientType: LoginClientTypeValue = "DESKTOP_WEB";
  if (/ipad|tablet/.test(normalizedUserAgent)) {
    clientType = "TABLET_WEB";
  } else if (/mobi|android|iphone/.test(normalizedUserAgent)) {
    clientType = "MOBILE_WEB";
  }

  let browser = "Unknown";
  if (normalizedUserAgent.includes("edg/")) {
    browser = "Edge";
  } else if (normalizedUserAgent.includes("opr/") || normalizedUserAgent.includes("opera")) {
    browser = "Opera";
  } else if (normalizedUserAgent.includes("chrome/") && !normalizedUserAgent.includes("edg/")) {
    browser = "Chrome";
  } else if (normalizedUserAgent.includes("safari/") && !normalizedUserAgent.includes("chrome/")) {
    browser = "Safari";
  } else if (normalizedUserAgent.includes("firefox/")) {
    browser = "Firefox";
  }

  let os = "Unknown";
  if (normalizedUserAgent.includes("windows")) {
    os = "Windows";
  } else if (normalizedUserAgent.includes("android")) {
    os = "Android";
  } else if (normalizedUserAgent.includes("iphone") || normalizedUserAgent.includes("ipad") || normalizedUserAgent.includes("ios")) {
    os = "iOS";
  } else if (normalizedUserAgent.includes("mac os") || normalizedUserAgent.includes("macintosh")) {
    os = "macOS";
  } else if (normalizedUserAgent.includes("linux")) {
    os = "Linux";
  }

  return {
    clientType,
    browser,
    os,
    userAgent,
  };
}

export async function logSessionEvent({
  eventType,
  userId,
  sessionId,
  authToken,
}: LogSessionEventArgs): Promise<void> {
  if (!userId || !sessionId) {
    return;
  }

  const metadata = getLoginClientMetadata();

  try {
    await graphqlUserPool<unknown>({
      query: createLoginEventMutation,
      variables: {
        input: {
          userId,
          eventType,
          occurredAt: new Date().toISOString(),
          sessionId,
          sourceApp: "web",
          clientType: metadata.clientType,
          browser: metadata.browser,
          os: metadata.os,
          userAgent: metadata.userAgent,
          authMethod: "email_password",
          mfaUsed: false,
          ipHash: null,
          country: null,
          region: null,
          city: null,
        },
      },
      authToken,
    });
  } catch (error) {
    logToConsole("warn", `Failed to log ${eventType} login event:`, error);
  }
}
